import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import Input from '@mui/joy/Input';
import Stack from '@mui/joy/Stack';
import Sheet from '@mui/joy/Sheet';
import Button from '@mui/joy/Button';

import { CreditLogProps } from 'stores/credit_log_store';
import { TimeUI } from 'components/Date';

import Table from '@mui/joy/Table';
import Typography from '@mui/joy/Typography';
import Chip from '@mui/joy/Chip';
import Tooltip from '@mui/joy/Tooltip';

import { ClockProps } from 'stores/clock_store';

import { getIcon } from 'utils/misc';
import { pluralize } from 'utils/misc';
import { datetimeLocalFormat } from 'utils/misc';
import { capitalize } from 'utils/misc';
import LockedFeature from 'components/LockedFeature';

import IdentityAvatar from 'components/IdentityAvatar';

import useUserIdentity from 'hooks/useUserIdentity';
import useCreditLog from 'hooks/useCreditLog';
import useRoles from 'hooks/useRoles';
import useClock from 'hooks/useClock';
import Success from 'components/Success';

import { timeDuration } from 'utils/misc';
import moment from 'moment-timezone';


export const ClockSupportForm = observer(({ clock, narrow=false }: { clock: ClockProps, narrow?: boolean }) => {
    const { hasRole } = useRoles();
    const { currentIdentity } = useUserIdentity();
    const { editViaSupport, deleteViaSupport } = useClock();

    const [timeStart, setTimeStart] = useState(datetimeLocalFormat(clock.time_start));
    const [timeEnd, setTimeEnd] = useState(datetimeLocalFormat(clock.time_end));
    const [originalDuration, setOriginalDuration] = useState(0);
    const [diff, setDiff] = useState(0);
    const [invalid, setInvalid] = useState(false);

    const MIN_CLOCK_DURATION = 60;
    const MAX_CLOCK_DURATION = 60 * 60 * 4;
    useEffect(() => {
        const start = new Date(timeStart);
        const end = new Date(timeEnd);
        const diff = end.getTime() - start.getTime();
        setDiff(diff / 1_000);
        setOriginalDuration(Math.floor(new Date(clock.time_end).getTime() - new Date(clock.time_start).getTime()) / 1_000);
        if ((diff / 1_000) < MIN_CLOCK_DURATION) {
            console.log("invalid", diff);
            setInvalid(true);
        }
        else {
            setInvalid(false);
        }
    }, [timeStart, timeEnd, setDiff, setInvalid, setOriginalDuration, clock]);


    function doSave() {
        const ts = moment(timeStart).utc().format();
        const te = moment(timeEnd).utc().format();
        editViaSupport(currentIdentity.uuid, clock.uuid, ts, te, "Edited via support")
            .then((res) => {
                if (!res) return;
                Success('Success! You may need to refresh this page to see it.');
                console.log("support update start", res.time_start);
                console.log("support update end", res.time_end);
            });
        console.log('doSave', clock.uuid);
    }


    function doDelete() {
        deleteViaSupport(currentIdentity.uuid, clock.uuid)
            .then((res) => {
                if (!res) return;
                Success('Success! You may need to refresh this page to see it.');
                console.log("support delete", res);
            });
    }

    if (!hasRole('support')) return null;

    return (
        <Stack spacing={1} >
        <Stack spacing={1}>

            <Stack spacing={1} direction={{ xs: 'column', sm: (narrow ? 'column': 'row' ) }} >
            <Stack direction={narrow ? "column" : "row"}>

                <Stack>
                    <Typography level="body-xs">
                        Start
                    </Typography>
                    <Input
                        onChange={(e) => setTimeStart(e.target.value)}
                        type="datetime-local" value={timeStart} />
                </Stack>

                <Stack>
                    <Typography level="body-xs">
                        End
                    </Typography>
                    <Input
                        onChange={(e) => setTimeEnd(e.target.value)}
                        type="datetime-local" value={timeEnd} />
                        </Stack>
                        </Stack>

                        <Stack justifyContent="flex-end" alignItems="flex-end" direction="row" spacing={1}>
                        {!invalid &&
                            <Button onClick={doSave}>Save</Button>
                    }
                    <Button color="danger" variant="outlined" onClick={() => { if(confirm("Are you sure?")) { doDelete() }}}>Delete</Button>
                    </Stack>

                        </Stack>

                <Stack>
                    <Typography level="body-xs">
                        Duration
                       </Typography>

                       <Stack direction="row" spacing={1} alignItems="stretch" >

                            <Typography
                                p={1}
                                level="body-sm"
                                variant="soft">
                                From: {timeDuration(originalDuration)}
                            </Typography>

                            <Typography
                                p={1}
                                color={invalid ? 'danger' : 'success'}
                                level="body-sm"
                                variant="soft">
                                To: {timeDuration(diff)}
                                </Typography>
                                </Stack>

                </Stack>

                {Math.abs(Math.floor(diff - originalDuration)) > 60 &&
                    <Stack>
                        <Typography level="body-xs">
                            Expected Change
                        </Typography>

                        <Typography
                            p={1}
                            color={(diff - originalDuration) ? 'danger' : 'success'}
                            level="body-sm"
                        >
                            {(diff - originalDuration) ? "Add" : "Remove"} {' '}
                            {timeDuration(diff - originalDuration)}
                        </Typography>
                    </Stack>
                }
            </Stack>

            {
                (diff < MIN_CLOCK_DURATION) &&
                <Typography level="body-xs" color="danger">
                    The duration is less than {MIN_CLOCK_DURATION / 60} minute.
                </Typography>
            }

            {
                (diff > MAX_CLOCK_DURATION) &&
                <Typography level="body-xs" color="danger">
                    The duration is longer than {MAX_CLOCK_DURATION / 60} minutes.
                </Typography>
            }


        </Stack >
    );
});

export const ClockSupportOptions = observer(({ clock, narrow=false }: { clock: ClockProps, narrow?: boolean }) => {
    const { hasRole } = useRoles();

const [locked, setLocked] = useState(true);
    if (!hasRole('support')) {
        return null;
    }
    return (
        <LockedFeature isLocked={locked} setIsLocked={setLocked} copy="Adjust">
        <ClockSupportForm narrow={narrow} clock={clock} />
        </LockedFeature>
    );
});



const ClockToolTip = observer(({ clock_uuid }: { clock_uuid: string }) => {
    const { rolesNames } = useRoles();
    const { fetchClock } = useClock();
    const { currentIdentity } = useUserIdentity();
    const [clock, setClock] = useState<ClockProps | null>(null);

    const isSupport = rolesNames.includes('support');
    useEffect(() => {
        if (!clock_uuid) return;
        fetchClock(currentIdentity.key, clock_uuid).then((r) => {
            setClock(r);
        });
    }, [clock_uuid, fetchClock]);
    if (!clock) return <em>Loading</em>
    return (
        <Sheet>
            <Stack
                m={3}
                spacing={3}
            >
                <Stack>
                    <IdentityAvatar
                        size="inline"
                        interactive={true}
                        identityKey={clock.identity} />
                    <Link to={`/${clock.associated_type}/${clock.associated_uuid}`}>
                        {capitalize(clock.associated_type)}
                    </Link>

                </Stack>
                <Stack>
                    <Typography level="body-xs">
                        Start
                    </Typography>
                    <Typography level="body-sm">
                        <TimeUI ts={clock.time_start} />
                    </Typography>
                </Stack>
                <Stack>
                    <Typography level="body-xs">
                        End
                    </Typography>
                    <Typography level="body-sm">
                        <TimeUI ts={clock.time_end} />
                    </Typography>
                </Stack>
            </Stack>
            {isSupport && clock && <ClockSupportOptions clock={clock} />}
        </Sheet>
    );
});

const ClockChip = observer(({ clock_uuid }: { clock_uuid: string }) => {
    const ClockIcon = getIcon('PunchClock');
    const [tooltipOpen, setTooltipOpen] = useState(false);

    return (
        <Tooltip
            open={tooltipOpen}
            variant="outlined"
            arrow
            title={<ClockToolTip clock_uuid={clock_uuid} />}>
            <Chip
                onClick={() => setTooltipOpen(!tooltipOpen)}
                startDecorator={<ClockIcon />}>Collab</Chip>
        </Tooltip>
    );
});


const SourceViewer = observer(({ item }: { item: CreditLogProps }) => {
    const PaymentIcon = getIcon('LocalAtm');

    const OverageIcon = getIcon('Timer');
    const AdjustmentIcon = getIcon('EditCalendar');
    const ConvertIcon = getIcon('PublishedWithChanges');
    const source = item.source;

    if (source === 'payment') {
        return <Tooltip arrow title="We added time due to your payment.">
            <Chip
                color="success"
                startDecorator={<PaymentIcon />}>Payment</Chip>
        </Tooltip>;
    }
    else if (source === 'clock') {
        return <Tooltip arrow title={"An assistant was clocked in on your collab for " + pluralize(item.acct_debit, 'minute')}>
            <ClockChip clock_uuid={item.source_id} />
        </Tooltip>;
    }
    else if (source === 'overage') {
        return <Tooltip arrow title="The system made an adjustment because you used more credits than you had.">
            <Chip color="warning" startDecorator={<OverageIcon />}>Overage</Chip>
        </Tooltip>;
    }
    else if (source === 'adjustment') {
        return <Tooltip arrow title="Support made an adjustment.">
            <Chip color="warning" startDecorator={<AdjustmentIcon />}>Adjustment</Chip>
        </Tooltip>
    }
    else if (source === 'task-convert') {
        return <Tooltip arrow title="You converted a Fancy Hands task into minutes.">
            <Chip color="success" startDecorator={<ConvertIcon />}>Task</Chip>
        </Tooltip>
    }
    else {
        return <Chip color="warning">{source}</Chip>;
    }


});

const CreditLog = observer(() => {
    const { fetchCreditLog, logItems } = useCreditLog();

    useEffect(() => {
        console.log('fetching credit log for user');
        fetchCreditLog();
    }, [fetchCreditLog]);
    return <CreditLogList logItems={logItems} />;
});

export const CreditLogList = observer(({ logItems }: { logItems: CreditLogProps[] }) => {

    return (
        <Stack m={2}>

            <Typography level="title-lg">
                Credit Log
            </Typography>
            <Table>
                <thead>
                    <tr>
                        <th>Date</th>
                        <th>Debit/Credit</th>
                        <th>Source</th>
                    </tr>
                </thead>
                {logItems.slice().reverse().map((item) => (
                    <tr key={item.uuid}>
                        <td><TimeUI ts={item.date_created} /></td>
                        <td>
                            {item.acct_debit > 0 ?
                                <Typography
                                    startDecorator="&minus;">
                                    {item.acct_debit}</Typography> :
                                <Typography
                                    startDecorator="&#43;">
                                    {item.acct_credit}</Typography>
                            }
                        </td>

                        <td><SourceViewer item={item} /></td>
                    </tr>
                ))}
            </Table>
        </Stack>

    );
});
export default CreditLog;
